import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import client from '@/apolloClient';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import DatePickerField from '@/components/fields/DatePickerField';
import SelectField from '@/components/fields/SelectField';
import SwitchField from '@/components/fields/SwitchField';
import { useActiveRole, useUser } from '@/contexts/UserSession';
import { UserRole } from '@/gql/enums';
import type { PerformanceResultUserFragment, SubmitSocResultInput } from '@/gql/graphql';
import { mapPassFailWithNullToResult } from '@/utils/common';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';
import useCompanyRoleLabels from '@/utils/useCompanyRoleLabels';
import type { AssessmentRunOption } from '../../shared/utils';
import SocInnerForm from '../SocInnerForm';
import type { CreateSocFormData } from '../SocPerformanceFormConfig';
import { createSocFormSchema, socDefaultFormValues } from '../SocPerformanceFormConfig';
import { SubmitSocPerformanceResultMutation } from './CreatePerformanceSocResult.graphql';

interface CreatePerformanceSocModalProps {
    open: boolean;
    onClose: (refresh?: boolean) => void;
    groupUsers: PerformanceResultUserFragment[];
    assessmentRunOptions: AssessmentRunOption[];
}

const CreatePerformanceSocModal = ({
    open,
    onClose,
    groupUsers,
    assessmentRunOptions,
}: CreatePerformanceSocModalProps) => {
    const { t } = useTranslation('performanceResult');
    const { getRoleLabel } = useCompanyRoleLabels();
    const user = useUser(true);
    const activeRole = useActiveRole();

    const isTrainee = useMemo(() => {
        return activeRole && activeRole.role === UserRole.Trainee;
    }, [activeRole]);

    const form = useForm<CreateSocFormData>({
        mode: 'onChange',
        resolver: zodResolver(createSocFormSchema),
        defaultValues: { ...socDefaultFormValues, userId: isTrainee ? user.id : undefined },
    });

    const isTiedToAssessmentRun = form.watch('isTiedToAssessmentRun');

    useEffect(() => {
        if (!isTiedToAssessmentRun) {
            form.setValue('assessmentRunId', '');
        }
    }, [form, isTiedToAssessmentRun]);

    const handleSubmit = useCallback(async () => {
        if (!(await form.trigger())) {
            return;
        }

        const values = form.getValues();

        const request: SubmitSocResultInput = {
            assessmentRunId: values.isTiedToAssessmentRun ? values.assessmentRunId : undefined,
            userId: values.userId,
            completedAt: values.date,
            result: {
                timingSec: values.timingSec ?? undefined,
                result: mapPassFailWithNullToResult(values.result),
            },
        };

        try {
            const { data } = await client.mutate({
                mutation: SubmitSocPerformanceResultMutation,
                variables: {
                    input: request,
                },
            });

            if (data?.submitSocResult?.__typename === 'ErrorResponse') {
                const errorResponse = data.submitSocResult;
                const specificErrorField = errorResponse.fields?.find(f => f.message);
                const errorMessage = specificErrorField?.message || t('createError.description', { type: 'SOC' });

                showErrorNotification({
                    message: t('createError.title', { type: 'SOC' }),
                    description: errorMessage,
                });
                throw new Error(errorMessage);
            } else {
                showSuccessNotification({
                    message: t('createSuccess.title', { type: 'SOC' }),
                    description: t('createSuccess.message', { type: 'SOC' }),
                });

                onClose(true);
            }
        } catch (error) {
            console.error('Error submitting SOC performance result:', error);
            throw error;
        }
    }, [form, onClose, t]);

    const userOptions = useMemo(() => {
        const options = [];
        for (const user of groupUsers) {
            if (user.__typename === 'TenantUser') {
                options.push({ label: user.name, value: user.id });
            }
        }
        return options;
    }, [groupUsers]);

    const modalFooter = (
        <div className="mt-5 flex w-full justify-end">
            <div className="flex space-x-2">
                <Button variant="secondary" onClick={() => onClose()}>
                    {t('soc.createModal.cancelBtn')}
                </Button>
                <Button variant="primary" onClick={handleSubmit}>
                    {t('soc.createModal.saveBtn')}
                </Button>
            </div>
        </div>
    );

    return (
        <FormProvider {...form}>
            <Modal
                open={open}
                onClose={onClose}
                title={t('soc.createModal.title')}
                footer={modalFooter}
                showCloseButton={false}
                className="w-full sm:w-xl"
                largeTitle
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 sm:grid-cols-3">
                        <div className="sm:col-span-3">
                            <SelectField
                                name="userId"
                                label={t('soc.form.traineeName', { role: getRoleLabel(UserRole.Trainee) })}
                                placeholder={t('soc.form.traineeNamePlaceholder', {
                                    role: getRoleLabel(UserRole.Trainee).toLocaleLowerCase(),
                                })}
                                searchable
                                options={userOptions}
                                required
                                disabled={!!isTrainee}
                            />
                        </div>
                        <div className="sm:col-span-3">
                            <DatePickerField name="date" label={t('soc.form.assessmentDate')} required />
                        </div>
                        <SocInnerForm />
                        <div className="sm:col-span-3">
                            <SwitchField
                                name="isTiedToAssessmentRun"
                                label={t('soc.form.tieToAssessmentRun', {
                                    defaultValue: 'Tie To Assessment Run',
                                })}
                            />
                        </div>
                        {isTiedToAssessmentRun && (
                            <div className="sm:col-span-3">
                                <SelectField
                                    name="assessmentRunId"
                                    label={t('filter.assessmentRun')}
                                    placeholder={t('filter.assessmentRunPlaceholder')}
                                    options={assessmentRunOptions}
                                    required
                                />
                            </div>
                        )}
                    </div>
                </form>
            </Modal>
        </FormProvider>
    );
};

export default CreatePerformanceSocModal;
